import React, { useEffect, useState } from 'react';
import { ErrorComponents, PageTabs } from '@css-xp/base';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { CustomPanel } from '@css-xp/base/custom-panel';
import { InvoiceGroupsTab, ShipToAddressTab, PaymentMethodsTab } from '../../../tabs/invoice-group-tabs';
import { BillingAdminsTab } from '../../../tabs/billing-contact-tab';
import './transaction-account-details-tab.scss';
import { useLocation, useHistory } from 'react-router-dom';
import kebabCase from 'lodash/kebabCase';
import defaultTo from 'lodash/defaultTo';
import concat from 'lodash/concat';
import { CommerceAddress, Maybe, PaymentMethod } from '@css-xp/graphtypes/src/generated-types-new';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { fetchTXAPartnerAccount } from '../queries/fetch-partner-account';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';

interface Props {
  transactionAccountId: string;
  label: string;
  id: string;
  invoiceGroupId?: string;
  billToAddress?: CommerceAddress | null;
  addBillingAdminsSidebarType: string;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  transactionAccountNumber?: string;
  updatePaymentMethodLink?: string;
  transactionAccountDisplayName?: string;
  partnerAccountId?: generatedTypesNew.Maybe<string> | undefined;
  currentTermAccountStatus?: generatedTypesNew.Maybe<string> | undefined;
}

// TODO: Need to use featureFlag once that is available
const isInvoiceGroupsEnabled = true;

export const panelText = 'billing admins, sold-to addresses, payment methods, and billing profiles';

export const TABS = ['Billing admins', 'Sold-to addresses', 'Payment methods', 'Billing profiles'];

export const TABS_HASH = TABS.map((tabName) => kebabCase(tabName));

const inner_tab_search_label = 'inner_tab';

const getInnerTabValue = (searchUrl?: string) => {
  const params = new URLSearchParams(searchUrl);
  return defaultTo(params?.get(inner_tab_search_label), '');
};

const TransactionAccountDetailsTab = ({
  label,
  id,
  billToAddress,
  onComplete,
  transactionAccountId,
  invoiceGroupId,
  addBillingAdminsSidebarType,
  availablePaymentMethods,
  transactionAccountNumber,
  updatePaymentMethodLink,
  transactionAccountDisplayName,
  partnerAccountId,
  currentTermAccountStatus,
}: Props) => {
  const location = useLocation();
  const history = useHistory();
  const [selectedTabKey, setSelectedTabKey] = useState<string>(getInnerTabValue(location?.search));

  const { data } = useQuery<
    generatedTypesNew.FetchTxaPartnerAccountQuery,
    generatedTypesNew.FetchTxaPartnerAccountQueryVariables
  >(fetchTXAPartnerAccount, {
    variables: { id: partnerAccountId ?? '' },
    fetchPolicy: 'no-cache',
    skip: !partnerAccountId,
  });

  useEffect(() => {
    if (location.search) {
      const innerTab = getInnerTabValue(location.search);
      if (innerTab) {
        setSelectedTabKey(kebabCase(innerTab));
      }
    }
  }, [location]);

  const validDomains = concat(data?.txaPartnerAccount?.domains?.alternates, data?.txaPartnerAccount?.domains?.main);

  const handleTabSelect = (tabLabel: string) => {
    history.push({
      hash: location?.hash,
      search: new URLSearchParams({ [inner_tab_search_label]: kebabCase(tabLabel) }).toString(),
    });
  };

  const getAccountDetailsTabs = () => {
    return (
      <PageTabs
        className="transaction-account-details-tab-container"
        showAllTab={false}
        onTabSelect={handleTabSelect}
        selectedTabKey={selectedTabKey}
      >
        <BillingAdminsTab
          id={id}
          transactionAccountId={transactionAccountId}
          invoiceGroupId={invoiceGroupId}
          billToAddress={billToAddress}
          entityName={'transactionAccount'}
          label={TABS[0]}
          addBillingAdminsSidebarType={addBillingAdminsSidebarType}
          onComplete={onComplete}
          transactionAccountNumber={transactionAccountNumber}
          validDomains={validDomains}
          isPartnerAccount={!!partnerAccountId}
        />
        <ShipToAddressTab
          label={TABS[1]}
          transactionAccountId={transactionAccountId}
          billToAddress={billToAddress}
          onComplete={onComplete}
          availablePaymentMethods={availablePaymentMethods}
        />
        <PaymentMethodsTab
          label={TABS[2]}
          transactionAccountId={transactionAccountId}
          onComplete={onComplete}
          invoiceGroupId={invoiceGroupId}
          transactionAccountDisplayName={transactionAccountDisplayName}
          transactionAccountNumber={transactionAccountNumber}
          currentTermAccountStatus={currentTermAccountStatus}
        />
        <InvoiceGroupsTab
          label={TABS[3]}
          transactionAccountId={transactionAccountId}
          disabled={!isInvoiceGroupsEnabled}
          onComplete={onComplete}
          billToAddress={billToAddress}
          availablePaymentMethods={availablePaymentMethods}
          updatePaymentMethodLink={updatePaymentMethodLink}
          termPaymentStatus={currentTermAccountStatus}
        />
      </PageTabs>
    );
  };

  return (
    <div className="transaction-account-details-tab" data-testid="transaction-account-details-tab">
      <CustomPanel
        heading={label}
        expandedHeader={`Collapse to hide ${panelText}`}
        collapsedHeader={`Expand to see ${panelText}`}
        isExpanded={true}
      >
        {getAccountDetailsTabs()}
      </CustomPanel>
    </div>
  );
};

export default TransactionAccountDetailsTab;
